import React from 'react';
import { useServices } from 'ha/services';
import { UserDocumentsResponse } from '@ha/api/v2/getUserDocuments';
import {
  UserDocument,
  UserDocumentCategory,
} from '@ha/api/v2/types/UserDocument';

const useGetUserDocuments = () => {
  const { apiV2 } = useServices();
  const [documents, setDocuments] =
    React.useState<UserDocumentsResponse | null>(null);

  const requestUserDocuments = React.useCallback(
    (categories?: UserDocumentCategory[], limit?: number, orderBy?: string) => {
      let query;
      if (categories?.length) {
        query = {
          categories: categories.join(','),
          ...(limit && { limit }),
          ...(orderBy && { orderBy }),
        };
      }
      apiV2
        .getUserDocuments('me', query)
        .then(data => {
          setDocuments(data.data);
        })
        .catch(e => {
          console.error(e);
        });
    },
    [apiV2],
  );

  const initUserDocuments = React.useCallback(
    (categories?: UserDocumentCategory[], limit?: number, orderBy?: string) => {
      if (!documents) {
        requestUserDocuments(categories, limit, orderBy);
      }
    },
    [requestUserDocuments, documents],
  );

  const getDocumentsByCategory = React.useCallback(
    (category: UserDocumentCategory) => {
      if (!documents) {
        return null;
      }

      return documents.documents.filter(
        document => document.category === category,
      );
    },
    [documents],
  );

  const addDocumentFile = React.useCallback((newFile: UserDocument) => {
    setDocuments(prevState => {
      if (prevState) {
        return {
          count: prevState.count + 1,
          documents: [...prevState.documents, newFile],
        };
      }
      return null;
    });
  }, []);

  const editDocumentFile = React.useCallback((uuid: string, title: string) => {
    setDocuments(prevState => {
      if (prevState) {
        // Edit the state to rename the file's title
        const newDocuments = prevState.documents.map(document =>
          document.uuid === uuid ? { ...document, title } : document,
        );
        return {
          count: prevState.count,
          documents: newDocuments,
        };
      }
      return null;
    });
  }, []);

  const deleteDocumentFile = React.useCallback((uuid: string) => {
    setDocuments(prevState => {
      if (prevState) {
        // Remove the deleted document from state
        const newDocuments = prevState.documents.filter(
          document => document.uuid !== uuid,
        );
        return {
          count: prevState.count - 1,
          documents: newDocuments,
        };
      }
      return null;
    });
  }, []);

  return {
    initUserDocuments,
    getDocumentsByCategory,
    addDocumentFile,
    editDocumentFile,
    deleteDocumentFile,
    requestUserDocuments,
    documents,
  };
};

export { useGetUserDocuments };
